/**
 * Subscription Manager
 * Extracted from WebSocketManager.ts
 * Manages project subscriptions and filtering
 */

import { log } from "@/shared/utils/logger";

export class SubscriptionManager {
  private subscribedProjects: Set<string> = new Set();

  // Add project subscription
  subscribe(projectId: string): void {
    if (!this.subscribedProjects.has(projectId)) {
      this.subscribedProjects.add(projectId);
      log.debug(
        "WebSocket",
        `Tracking subscription for project ${projectId}. Total subscriptions: ${this.subscribedProjects.size}`
      );
    }
  }

  // Remove project subscription
  unsubscribe(projectId: string): boolean {
    if (this.subscribedProjects.has(projectId)) {
      this.subscribedProjects.delete(projectId);
      log.debug(
        "WebSocket",
        `Stopped tracking subscription for project ${projectId}. Remaining subscriptions: ${this.subscribedProjects.size}`
      );
      return true;
    }
    log.debug(
      "WebSocket",
      `Not subscribed to project ${projectId}, skipping unsubscribe`
    );
    return false;
  }

  // Check if subscribed to project
  isSubscribed(projectId: string): boolean {
    return this.subscribedProjects.has(projectId);
  }

  // Get all subscribed projects
  getSubscribedProjects(): string[] {
    return Array.from(this.subscribedProjects);
  }

  // Get first subscribed project
  getFirstProject(): string | null {
    const projects = Array.from(this.subscribedProjects);
    return projects.length > 0 ? projects[0] : null;
  }

  // Get subscription count
  getSubscriptionCount(): number {
    return this.subscribedProjects.size;
  }

  // Check if message should be filtered based on subscriptions
  shouldFilterMessage(
    messageProjectId: string | undefined,
    messageUserId: string | undefined,
    currentUserId: string | null
  ): boolean {
    if (!messageProjectId) {
      return false; // Don't filter messages without project ID
    }

    if (!this.subscribedProjects.has(messageProjectId)) {
      // Only filter if it's not our own user's message (for cross-project visibility)
      if (messageUserId !== currentUserId) {
        log.debug(
          "WebSocket",
          `❌ Message FILTERED - not subscribed to project ${messageProjectId}`
        );
        log.debug(
          "WebSocket",
          `Currently subscribed to: ${Array.from(this.subscribedProjects).join(", ")}`
        );
        return true;
      }
    }

    return false;
  }

  // Clear all subscriptions
  clear(): void {
    this.subscribedProjects.clear();
  }
}
